import React, { useEffect, useState } from 'react';
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import {
  Close as CloseIcon,
  CampaignOutlined as CampaignIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { SHOWS_HUB } from '../config/marketingEvents';

/**
 * Site-wide announcement bar for active trade shows.
 * Content + on/off are controlled from Admin → Trade shows.
 * Dismiss hides it for this visit only.
 */
const WhatsNewBanner = () => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const [banner, setBanner] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/marketing/banner');
        if (!cancelled) {
          setBanner(data?.banner || null);
        }
      } catch {
        if (!cancelled) {
          setBanner(null);
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDismiss = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDismissed(true);
  };

  if (!loaded || dismissed || !banner?.enabled) return null;

  const href = banner.href || SHOWS_HUB.path;
  const message = banner.message || t('home.whatsNew.message');
  const messageShort = banner.messageShort || t('home.whatsNew.messageShort');
  const cta = banner.cta || t('home.whatsNew.cta');

  return (
    <Box
      component={RouterLink}
      to={href}
      data-testid="wtm-whats-new-banner"
      aria-label={`${t('home.whatsNew.badge')}: ${cta}`}
      sx={{
        display: 'block',
        textDecoration: 'none',
        color: '#fff',
        position: 'relative',
        zIndex: (theme) => theme.zIndex.appBar + 1,
        overflow: 'hidden',
        background: 'linear-gradient(105deg, #3d100f 0%, #591716 42%, #8a2f2c 72%, #1a1208 100%)',
        borderBottom: '3px solid #E8C547',
        boxShadow: '0 8px 28px rgba(89, 23, 22, 0.45)',
        px: { xs: 1.5, sm: 2.5 },
        py: { xs: 1.15, sm: 1.85 },
        cursor: 'pointer',
        transition: 'transform 0.2s ease, filter 0.2s ease',
        '@keyframes whatsNewShimmer': {
          '0%': { transform: 'translateX(-120%)' },
          '100%': { transform: 'translateX(220%)' },
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '40%',
          height: '100%',
          background:
            'linear-gradient(90deg, transparent, rgba(232,197,71,0.18), transparent)',
          animation: 'whatsNewShimmer 4.5s ease-in-out infinite',
          pointerEvents: 'none',
        },
        '&:hover': {
          filter: 'brightness(1.06)',
          '& .whats-new-cta': {
            bgcolor: '#fff',
            color: '#591716',
            transform: 'translateX(2px)',
          },
        },
        '&:focus-visible': {
          outline: '3px solid #E8C547',
          outlineOffset: -3,
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={{ xs: 1, sm: 2 }}
        sx={{ maxWidth: 1140, mx: 'auto', position: 'relative', zIndex: 1, pr: { xs: 4, sm: 5 } }}
      >
        <Chip
          icon={<CampaignIcon sx={{ fontSize: 17, color: '#1a1208 !important' }} />}
          label={t('home.whatsNew.badge')}
          size="small"
          sx={{
            bgcolor: '#E8C547',
            color: '#1a1208',
            fontWeight: 800,
            fontSize: '0.72rem',
            letterSpacing: 0.3,
            height: 28,
            flexShrink: 0,
            pointerEvents: 'none',
            boxShadow: '0 2px 10px rgba(232,197,71,0.45)',
            '& .MuiChip-icon': { ml: 0.75 },
            '& .MuiChip-label': { px: 1 },
          }}
        />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: { xs: '0.85rem', sm: '0.98rem' },
            lineHeight: 1.35,
            flex: 1,
            minWidth: 0,
            textShadow: '0 1px 8px rgba(0,0,0,0.35)',
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {message}
          </Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
            {messageShort}
          </Box>
        </Typography>
        <Box
          className="whats-new-cta"
          component="span"
          sx={{
            display: { xs: 'none', sm: 'inline-flex' },
            alignItems: 'center',
            gap: 0.75,
            flexShrink: 0,
            fontWeight: 800,
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
            bgcolor: '#E8C547',
            color: '#1a1208',
            borderRadius: 999,
            px: { xs: 1.5, sm: 2 },
            py: 0.7,
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
            transition: 'background-color 0.2s ease, color 0.2s ease, transform 0.2s ease',
          }}
        >
          {cta}
          <ArrowIcon sx={{ fontSize: 17 }} />
        </Box>
        <IconButton
          aria-label={t('home.whatsNew.dismiss')}
          onClick={handleDismiss}
          onMouseDown={(event) => event.stopPropagation()}
          size="small"
          sx={{
            position: 'absolute',
            right: 0,
            color: 'rgba(255,255,255,0.9)',
            '&:hover': { color: '#E8C547', bgcolor: 'rgba(255,255,255,0.12)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
};

export default WhatsNewBanner;
